<template>
  <div class="bg-background text-foreground min-h-screen">
    <div class="w-full px-4 sm:px-6 py-5">

      <!-- Page header -->
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-5">
        <div class="flex items-start gap-3 min-w-0">
          <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <i class="fas fa-file-alt text-primary text-lg"></i>
          </div>
          <div class="min-w-0">
            <h1 class="text-xl font-bold leading-tight">My Applications</h1>
            <p class="text-sm text-muted-foreground leading-snug">Track progress, review cover letters, and keep momentum.</p>
          </div>
        </div>

        <!-- Search + sort controls -->
        <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-shrink-0">
          <div class="relative">
            <i class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs"></i>
            <input
              v-model="searchQuery"
              type="text"
              class="h-10 pl-9 pr-3 w-full sm:w-64 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
              placeholder="Search job title or employer..."
            />
          </div>
          <div class="flex items-center gap-2">
            <select
              v-model="sortKey"
              class="h-10 px-3 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
              aria-label="Sort applications"
            >
              <option value="applied_date">Newest first</option>
              <option value="job_title">Job title</option>
              <option value="employer_name">Employer</option>
              <option value="status">Status</option>
            </select>
            <button
              type="button"
              @click="toggleSortDirection"
              class="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted text-sm font-medium text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              :aria-label="sortDir === 'desc' ? 'Descending' : 'Ascending'"
              title="Toggle sort direction"
            >
              <i :class="sortDir === 'desc' ? 'fas fa-arrow-down' : 'fas fa-arrow-up'"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Loading skeleton -->
      <div v-if="loading" class="bg-card border border-border rounded-xl overflow-hidden">
        <div class="px-5 py-3.5 border-b border-border">
          <div class="h-3 w-32 rounded bg-muted animate-pulse"></div>
        </div>
        <div class="px-5 py-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div v-for="n in 6" :key="n" class="rounded-xl border border-border bg-card p-4">
            <div class="h-3.5 w-2/3 rounded bg-muted animate-pulse"></div>
            <div class="mt-3 h-3 w-1/2 rounded bg-muted animate-pulse"></div>
            <div class="mt-5 flex items-center justify-between">
              <div class="h-5 w-20 rounded-full bg-muted animate-pulse"></div>
              <div class="h-8 w-20 rounded-lg bg-muted animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error banner -->
      <div
        v-else-if="error"
        class="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2 mb-4"
      >
        <i class="fas fa-exclamation-circle text-red-500 text-sm mt-0.5 flex-shrink-0"></i>
        <div class="flex-1">
          <div class="text-[12px] font-semibold text-red-900">Something went wrong</div>
          <div class="text-[12px] text-red-900 mt-0.5">{{ error }}</div>
          <div class="mt-3">
            <button
              type="button"
              @click="fetchApplications"
              class="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold"
            >
              <i class="fas fa-rotate-right text-xs"></i>
              Retry
            </button>
          </div>
        </div>
      </div>

      <!-- Empty state — no applications at all -->
      <div
        v-else-if="applications.length === 0"
        class="bg-card border border-border rounded-xl p-12 text-center"
      >
        <div class="mx-auto mb-3 h-14 w-14 rounded-full bg-muted flex items-center justify-center">
          <i class="fas fa-file-alt text-muted-foreground text-2xl"></i>
        </div>
        <h3 class="text-base font-bold text-foreground">No applications yet</h3>
        <p class="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">
          When you apply to jobs, your history and progress will appear here.
        </p>
        <router-link
          to="/job-filters"
          class="mt-5 inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold"
        >
          <i class="fas fa-briefcase text-xs"></i>
          Browse Jobs
        </router-link>
      </div>

      <!-- Applications list -->
      <div v-else class="bg-card border border-border rounded-xl overflow-hidden">

        <!-- Card header: title + status filter tabs -->
        <div class="px-5 py-3.5 border-b border-border flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div class="text-sm font-bold">Applications</div>
            <div class="text-[11px] text-muted-foreground">
              <span class="font-semibold text-foreground">{{ applications.length }}</span> total
            </div>
          </div>
          <!-- Segment-control pill tabs -->
          <div class="flex items-center gap-1 bg-muted/50 border border-border rounded-lg p-1 overflow-x-auto flex-shrink-0 max-w-full">
            <button
              v-for="filter in statusFilters"
              :key="filter.value"
              @click="activeFilter = filter.value"
              :class="[
                'inline-flex items-center gap-1.5 px-3 h-7 rounded-md text-xs font-semibold whitespace-nowrap transition flex-shrink-0',
                activeFilter === filter.value
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted',
              ]"
            >
              <span>{{ filter.label }}</span>
              <span class="text-[10px] font-bold opacity-70">{{ getStatusCount(filter.value) }}</span>
            </button>
          </div>
        </div>

        <!-- Empty filter state -->
        <div
          v-if="displayedApplications.length === 0"
          class="px-5 py-12 text-center"
        >
          <div class="mx-auto mb-3 h-14 w-14 rounded-full bg-muted flex items-center justify-center">
            <i class="fas fa-filter text-muted-foreground text-xl"></i>
          </div>
          <div class="text-sm font-bold text-foreground">No matches</div>
          <div class="mt-1 text-sm text-muted-foreground">Try changing the filter or search terms.</div>
          <button
            type="button"
            @click="resetFilters"
            class="mt-4 h-9 px-3 rounded-lg border border-border bg-card hover:bg-muted text-sm font-medium text-muted-foreground"
          >
            Clear filters
          </button>
        </div>

        <template v-else>
          <!-- Mobile cards -->
          <div class="grid grid-cols-1 gap-0 divide-y divide-border lg:hidden">
            <div
              v-for="application in paginatedApplications"
              :key="application.id"
              class="bg-card p-4 hover:bg-muted/30 transition cursor-pointer"
              @click="viewCoverLetter(application)"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-1.5">
                    <span v-if="getSourceLogo(application.job_source)" class="h-4 w-4 rounded-full bg-white dark:bg-white/90 ring-1 ring-border/60 flex items-center justify-center shrink-0 overflow-hidden">
                      <img :src="getSourceLogo(application.job_source)" :alt="getSourceLabel(application.job_source)" class="h-full w-full object-contain" />
                    </span>
                    <span class="block text-sm font-semibold truncate">{{ application.job_title }}</span>
                  </div>
                  <div class="mt-0.5 text-xs text-muted-foreground truncate">
                    {{ application.employer_name || "Unknown Employer" }}
                  </div>
                  <div class="mt-1 flex items-center gap-2 flex-wrap">
                    <span class="text-xs text-muted-foreground">Applied {{ formatDate(application.applied_date) }}</span>
                    <span
                      v-if="application.has_employer_reply"
                      class="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                    >
                      <i class="fas fa-reply text-[8px]"></i> Replied
                    </span>
                  </div>
                </div>

                <span
                  class="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border flex-shrink-0"
                  :class="getStatusPillClass(application.status)"
                >
                  {{ getStatusLabel(application.status) }}
                </span>
              </div>

              <div class="mt-3 flex items-center gap-2" @click.stop>
                <div class="relative">
                  <select
                    v-model="application.status"
                    @change="updateStatus(application)"
                    class="app-status-select"
                    :class="getStatusSelectClass(application.status)"
                    :style="getStatusSelectStyle(application.status)"
                  >
                    <option v-for="option in statusChoices" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                  <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <i class="fas fa-chevron-down text-[10px]" :style="{ color: 'inherit' }"></i>
                  </div>
                </div>
                <button
                  v-if="application.cover_letter_content"
                  @click="viewCoverLetter(application)"
                  title="View cover letter"
                  class="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-border bg-card hover:bg-muted text-muted-foreground hover:text-primary transition"
                >
                  <i class="fas fa-file-signature text-xs"></i>
                </button>
                <button
                  v-if="application.source_thread_hash_id"
                  @click="viewMessages(application)"
                  title="View messages"
                  class="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground transition"
                >
                  <i class="fas fa-comments text-xs"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Desktop table -->
          <div class="hidden lg:block overflow-x-auto">
            <table class="min-w-full divide-y divide-border">
              <thead class="bg-muted/50 border-b border-border">
                <tr>
                  <th scope="col" class="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Job Title
                  </th>
                  <th scope="col" class="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Employer
                  </th>
                  <th scope="col" class="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Applied
                  </th>
                  <th scope="col" class="px-5 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody class="bg-card divide-y divide-border">
                <tr
                  v-for="application in paginatedApplications"
                  :key="application.id"
                  class="hover:bg-muted/30 transition cursor-pointer"
                  @click="viewCoverLetter(application)"
                >
                  <td class="px-5 py-3.5">
                    <div class="flex items-center gap-1.5">
                      <span v-if="getSourceLogo(application.job_source)" class="h-4 w-4 rounded-full bg-white dark:bg-white/90 ring-1 ring-border/60 flex items-center justify-center shrink-0 overflow-hidden">
                        <img :src="getSourceLogo(application.job_source)" :alt="getSourceLabel(application.job_source)" class="h-full w-full object-contain" />
                      </span>
                      <span class="text-sm font-semibold">{{ application.job_title }}</span>
                    </div>
                  </td>
                  <td class="px-5 py-3.5 text-sm text-foreground">
                    {{ application.employer_name || "Unknown Employer" }}
                  </td>
                  <td class="px-5 py-3.5 text-sm text-muted-foreground whitespace-nowrap">
                    {{ formatDate(application.applied_date) }}
                  </td>
                  <td class="px-5 py-3.5" @click.stop>
                    <div class="flex items-center gap-2">
                      <div class="relative">
                        <select
                          v-model="application.status"
                          @change="updateStatus(application)"
                          class="app-status-select"
                          :class="getStatusSelectClass(application.status)"
                          :style="getStatusSelectStyle(application.status)"
                          aria-label="Update status"
                        >
                          <option v-for="option in statusChoices" :key="option.value" :value="option.value">
                            {{ option.label }}
                          </option>
                        </select>
                        <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <i class="fas fa-chevron-down text-[10px]" :style="{ color: 'inherit' }"></i>
                        </div>
                      </div>
                      <button
                        v-if="application.cover_letter_content"
                        @click="viewCoverLetter(application)"
                        title="View cover letter"
                        class="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-border bg-card hover:bg-muted text-muted-foreground hover:text-primary transition"
                      >
                        <i class="fas fa-file-signature text-xs"></i>
                      </button>
                      <button
                        v-if="application.source_thread_hash_id"
                        @click="viewMessages(application)"
                        title="View messages"
                        class="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground transition"
                      >
                        <i class="fas fa-comments text-xs"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- Pagination bar -->
          <div
            v-if="totalPages > 1"
            class="px-5 py-3.5 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          >
            <div class="text-xs text-muted-foreground">
              Showing
              <span class="font-semibold text-foreground">{{ (currentPage - 1) * perPage + 1 }}</span>
              –
              <span class="font-semibold text-foreground">{{ Math.min(currentPage * perPage, displayedApplications.length) }}</span>
              of
              <span class="font-semibold text-foreground">{{ displayedApplications.length }}</span>
            </div>

            <div class="flex items-center gap-1">
              <!-- Prev -->
              <button
                :disabled="currentPage === 1"
                @click="currentPage--"
                class="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-border bg-card text-sm text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <i class="fas fa-chevron-left text-xs"></i>
              </button>

              <!-- Page numbers -->
              <template v-for="p in visiblePages" :key="p">
                <span
                  v-if="p === '...'"
                  class="h-8 w-8 inline-flex items-center justify-center text-xs text-muted-foreground"
                >…</span>
                <button
                  v-else
                  @click="currentPage = p"
                  class="h-8 w-8 inline-flex items-center justify-center rounded-lg border text-xs font-semibold transition"
                  :class="currentPage === p
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card text-muted-foreground hover:bg-muted'"
                >{{ p }}</button>
              </template>

              <!-- Next -->
              <button
                :disabled="currentPage === totalPages"
                @click="currentPage++"
                class="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-border bg-card text-sm text-muted-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <i class="fas fa-chevron-right text-xs"></i>
              </button>
            </div>

            <!-- Per-page selector -->
            <div class="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Rows</span>
              <select
                v-model="perPage"
                @change="currentPage = 1"
                class="h-8 px-2 rounded-lg border border-border bg-card text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="50">50</option>
              </select>
            </div>
          </div>
        </template>
      </div>

    </div>

    <!-- Status Update Modal -->
    <div
      v-if="showUpdateModal"
      class="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
    >
      <div class="bg-card border border-border rounded-xl max-w-md w-full overflow-hidden">
        <div class="px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 class="text-sm font-bold text-foreground">Update Application Status</h3>
          <button
            @click="showUpdateModal = false"
            class="h-8 w-8 rounded-md text-muted-foreground hover:bg-muted flex items-center justify-center"
          >
            <i class="fas fa-xmark text-sm"></i>
          </button>
        </div>

        <div class="px-5 py-4 space-y-4">
          <div>
            <div class="text-sm font-semibold text-foreground">{{ selectedApplication?.job_title }}</div>
            <div class="text-xs text-muted-foreground mt-0.5">{{ selectedApplication?.employer_name || "Unknown Employer" }}</div>
          </div>

          <div>
            <label class="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Status</label>
            <select
              v-model="updatedStatus"
              class="h-10 px-3 w-full rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              <option
                v-for="status in statusChoices"
                :key="status.value"
                :value="status.value"
              >
                {{ status.label }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Notes</label>
            <textarea
              v-model="updatedNotes"
              rows="3"
              class="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
              placeholder="Add any notes about this status update..."
            ></textarea>
          </div>

          <!-- Date fields based on status -->
          <div v-if="updatedStatus === 'interview'">
            <label class="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Interview Date</label>
            <input
              type="datetime-local"
              v-model="updatedInterviewDate"
              class="h-10 px-3 w-full rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          <div v-if="updatedStatus === 'offer'">
            <label class="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Offer Date</label>
            <input
              type="datetime-local"
              v-model="updatedOfferDate"
              class="h-10 px-3 w-full rounded-lg border border-border bg-card text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>

        <div class="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
          <button
            @click="showUpdateModal = false"
            class="h-9 px-3 rounded-lg border border-border bg-card hover:bg-muted text-sm font-medium text-muted-foreground"
          >
            Cancel
          </button>
          <button
            @click="updateApplicationStatus"
            class="inline-flex items-center gap-2 h-9 px-3.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold"
            :disabled="updating"
          >
            <i v-if="updating" class="fas fa-circle-notch animate-spin text-xs"></i>
            <span>{{ updating ? 'Updating...' : 'Update Status' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Toast notification -->
    <transition name="fade">
      <div
        v-if="showToast"
        class="fixed bottom-5 right-5 z-[70] bg-card border border-border rounded-xl px-4 py-3 flex items-start gap-3 min-w-[280px] max-w-[360px]"
      >
        <i
          :class="[
            'fas text-sm mt-0.5 flex-shrink-0',
            toastType === 'success'
              ? 'fa-check-circle text-emerald-500'
              : toastType === 'error'
              ? 'fa-exclamation-circle text-red-500'
              : 'fa-info-circle text-primary',
          ]"
        ></i>
        <div class="flex-1 min-w-0">
          <div class="text-sm font-semibold">{{ toastTitle }}</div>
          <div class="text-[12px] text-muted-foreground mt-0.5">{{ toastMessage }}</div>
        </div>
        <button @click="showToast = false" class="text-muted-foreground hover:text-foreground flex-shrink-0">
          <i class="fas fa-xmark text-xs"></i>
        </button>
      </div>
    </transition>

    <!-- Cover letter panel backdrop -->
    <transition name="fade">
      <div
        v-if="showCoverLetterPanel"
        @click="showCoverLetterPanel = false"
        class="fixed inset-0 z-50 bg-black/40"
      ></div>
    </transition>

    <!-- Cover letter slide-in panel -->
    <transition name="slide-sheet">
      <div
        v-if="showCoverLetterPanel"
        class="fixed top-0 right-0 h-full w-full max-w-[560px] z-[60] bg-card border-l border-border flex flex-col"
      >
        <!-- Panel header -->
        <div class="px-5 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
          <div class="flex items-center gap-3">
            <div class="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-file-signature text-primary"></i>
            </div>
            <div>
              <div class="text-sm font-bold">Cover Letter</div>
              <div class="text-[11px] text-muted-foreground truncate max-w-[280px]">
                {{ selectedCoverLetter?.jobTitle || "Application" }}
              </div>
            </div>
          </div>
          <button
            @click="showCoverLetterPanel = false"
            class="h-8 w-8 rounded-md text-muted-foreground hover:bg-muted flex items-center justify-center"
          >
            <i class="fas fa-xmark"></i>
          </button>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto p-5 space-y-4" style="scrollbar-width:thin">
          <!-- No cover letter -->
          <div v-if="!selectedCoverLetter?.content" class="flex flex-col items-center justify-center py-16 text-center">
            <i class="fas fa-file-slash text-muted-foreground text-2xl mb-3 opacity-40"></i>
            <div class="text-sm font-semibold text-muted-foreground">No cover letter available</div>
            <p class="text-[12px] text-muted-foreground mt-1">This application does not have a cover letter attached.</p>
          </div>

          <template v-else>
            <!-- Subject -->
            <div v-if="selectedCoverLetter.subject" class="rounded-lg border border-border bg-muted/30 px-3 py-2">
              <div class="text-[9px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Subject</div>
              <div class="text-[12px] font-semibold text-foreground">{{ selectedCoverLetter.subject }}</div>
            </div>

            <!-- Cover letter body -->
            <div>
              <div class="text-[9px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Cover Letter</div>
              <div class="rounded-xl border border-border bg-card p-4 text-[13px] leading-relaxed whitespace-pre-line text-foreground">{{ selectedCoverLetter.content }}</div>
            </div>
          </template>
        </div>

        <!-- Footer -->
        <div class="px-5 py-3 border-t border-border bg-muted/20 flex justify-end">
          <button
            @click="showCoverLetterPanel = false"
            class="h-9 px-4 rounded-lg border border-border bg-card hover:bg-muted text-sm font-semibold text-muted-foreground transition"
          >
            Close
          </button>
        </div>
      </div>
    </transition>

    <!-- Messages panel backdrop -->
    <transition name="fade">
      <div
        v-if="showMessagesPanel"
        @click="showMessagesPanel = false"
        class="fixed inset-0 z-50 bg-black/40"
      ></div>
    </transition>

    <!-- Messages slide-in panel -->
    <transition name="slide-sheet">
      <div
        v-if="showMessagesPanel"
        class="fixed top-0 right-0 h-full w-full max-w-[560px] z-[60] bg-card border-l border-border flex flex-col"
      >
        <!-- Panel header -->
        <div class="px-5 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
          <div class="flex items-center gap-3">
            <div class="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-comments text-primary"></i>
            </div>
            <div>
              <div class="text-sm font-bold">Thread Messages</div>
              <div class="text-[11px] text-muted-foreground truncate max-w-[280px]">
                {{ selectedOljPartner?.name || "Conversation" }}
              </div>
            </div>
          </div>
          <button
            @click="showMessagesPanel = false"
            class="h-8 w-8 rounded-md text-muted-foreground hover:bg-muted flex items-center justify-center"
          >
            <i class="fas fa-xmark"></i>
          </button>
        </div>

        <!-- Thread display -->
        <div class="flex-1 overflow-hidden">
          <OljThreadDisplay
            v-if="selectedOljHashId"
            :thread-data="oljThreadData"
            :loading="oljThreadLoading"
            :hash-id="selectedOljHashId"
            :partner-data="selectedOljPartner"
            :error-type="oljThreadError"
          />
          <div v-else class="p-8 text-center text-sm text-muted-foreground">
            No conversation available for this application.
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import apiClient from "@/services/api";
import { buildJobDetailsPath } from "@/utils/slug";
import OljThreadDisplay from "@/components/OljThreadDisplay.vue";

export default {
  name: "ApplicationsPage",
  components: {
    OljThreadDisplay,
  },
  setup() {
    // Applications data
    const applications = ref([]);
    const loading = ref(true);
    const error = ref(null);

    const searchQuery = ref("");
    const sortKey = ref("applied_date");
    const sortDir = ref("desc");
    const currentPage = ref(1);
    const perPage = ref(20);

    // Status choices from the model
    const statusChoices = [
      { value: "applied", label: "Applied" },
      { value: "reviewing", label: "Under Review" },
      { value: "interview", label: "Interview" },
      { value: "offer", label: "Offer" },
      { value: "hired", label: "Hired" },
      { value: "rejected", label: "Rejected" },
    ];

    // Messages panel (OLJ thread)
    const showMessagesPanel = ref(false);
    const selectedOljHashId = ref(null);
    const oljThreadData = ref(null);
    const oljThreadLoading = ref(false);
    const oljThreadError = ref(null);
    const selectedOljPartner = ref(null);

    // Cover letter slide-in panel
    const showCoverLetterPanel = ref(false);
    const selectedCoverLetter = ref(null);

    // Toast notification
    const showToast = ref(false);
    const toastType = ref("success");
    const toastTitle = ref("");
    const toastMessage = ref("");

    // Filter state
    const activeFilter = ref("all");
    const statusFilters = [
      { value: "all", label: "All Applications" },
      { value: "applied", label: "Applied" },
      { value: "reviewing", label: "Under Review" },
      { value: "interview", label: "Interview" },
      { value: "offer", label: "Offer" },
      { value: "hired", label: "Hired" },
      { value: "rejected", label: "Rejected" },
    ];

    // Computed properties
    const filteredApplications = computed(() => {
      if (activeFilter.value === "all") {
        return applications.value;
      }
      return applications.value.filter(
        (app) => app.status === activeFilter.value,
      );
    });

    const displayedApplications = computed(() => {
      const query = (searchQuery.value || "").trim().toLowerCase();
      let list = filteredApplications.value;

      if (query) {
        list = list.filter((app) => {
          const title = (app?.job_title || "").toLowerCase();
          const employer = (app?.employer_name || "").toLowerCase();
          return title.includes(query) || employer.includes(query);
        });
      }

      const normalized = [...list];
      const dir = sortDir.value === "asc" ? 1 : -1;

      normalized.sort((a, b) => {
        const key = sortKey.value;
        if (key === "applied_date") {
          const da = a?.applied_date ? new Date(a.applied_date).getTime() : 0;
          const db = b?.applied_date ? new Date(b.applied_date).getTime() : 0;
          return (da - db) * dir;
        }

        const va = (a?.[key] ?? "").toString().toLowerCase();
        const vb = (b?.[key] ?? "").toString().toLowerCase();
        return va.localeCompare(vb) * dir;
      });

      return normalized;
    });

    const totalPages = computed(() =>
      Math.max(1, Math.ceil(displayedApplications.value.length / perPage.value))
    );

    const paginatedApplications = computed(() => {
      const start = (currentPage.value - 1) * perPage.value;
      return displayedApplications.value.slice(start, start + perPage.value);
    });

    const visiblePages = computed(() => {
      const total = totalPages.value;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      const cur = currentPage.value;
      const pages = [];
      pages.push(1);
      if (cur > 3) pages.push('...');
      for (let p = Math.max(2, cur - 1); p <= Math.min(total - 1, cur + 1); p++) pages.push(p);
      if (cur < total - 2) pages.push('...');
      pages.push(total);
      return pages;
    });

    // Reset to page 1 when filters or search change
    watch([activeFilter, searchQuery, sortKey, sortDir], () => { currentPage.value = 1; });

    // Helper functions
    const getStatusLabel = (status) => {
      const found = statusChoices.find((choice) => choice.value === status);
      return found ? found.label : status;
    };

    const getStatusClass = (status) => {
      switch (status) {
        case "applied":
          return "bg-primary/10 text-primary";

        case "reviewing":
          return "bg-yellow-100 text-yellow-800";
        case "interview":
          return "bg-purple-100 text-purple-800";
        case "offer":
          return "bg-green-100 text-green-800";
        case "hired":
          return "bg-green-100 text-green-800";
        case "rejected":
          return "bg-red-100 text-red-800";
        default:
          return "bg-muted/50 text-foreground";
      }
    };

    const getStatusPillClass = (status) => {
      switch (status) {
        case "applied":
          return "border-primary/20 bg-primary/5 text-primary";
        case "reviewing":
          return "border-amber-200 bg-amber-50 text-amber-900";
        case "interview":
          return "border-purple-200 bg-purple-50 text-purple-900";
        case "offer":
          return "border-emerald-200 bg-emerald-50 text-emerald-900";
        case "hired":
          return "border-green-200 bg-green-50 text-green-900";
        case "rejected":
          return "border-red-200 bg-red-50 text-red-800";
        default:
          return "border-border bg-muted/30 text-foreground";
      }
    };

    const getStatusSelectClass = (status) => {
      switch (status) {
        case "applied":
          return "ring-blue-200/60 focus:ring-blue-200";
        case "reviewing":
          return "ring-amber-200/60 focus:ring-amber-200";
        case "interview":
          return "ring-purple-200/60 focus:ring-purple-200";
        case "offer":
          return "ring-emerald-200/60 focus:ring-emerald-200";
        case "hired":
          return "ring-green-200/60 focus:ring-green-200";
        case "rejected":
          return "ring-red-200/60 focus:ring-red-200";
        default:
          return "ring-gray-200/60 focus:ring-gray-200";
      }
    };

    const getStatusSelectStyle = (status) => {
      switch (status) {
        case "applied":
          return {
            backgroundColor: "#EFF6FF",
            borderColor: "#BFDBFE",
            color: "#1E40AF",
          };
        case "reviewing":
          return {
            backgroundColor: "#FFFBEB",
            borderColor: "#FDE68A",
            color: "#92400E",
          };
        case "interview":
          return {
            backgroundColor: "#F5F3FF",
            borderColor: "#DDD6FE",
            color: "#5B21B6",
          };
        case "offer":
          return {
            backgroundColor: "#ECFDF5",
            borderColor: "#A7F3D0",
            color: "#065F46",
          };
        case "hired":
          return {
            backgroundColor: "#F0FDF4",
            borderColor: "#BBF7D0",
            color: "#166534",
          };
        case "rejected":
          return {
            backgroundColor: "#FEF2F2",
            borderColor: "#FECACA",
            color: "#991B1B",
          };
        default:
          return {
            backgroundColor: "#F9FAFB",
            borderColor: "#E5E7EB",
            color: "#111827",
          };
      }
    };

    const SOURCE_LABELS = {
      olj: "External",
      linkedin: "LinkedIn",
      indeed: "Indeed",
      upwork: "Upwork",
      jobstreet: "JobStreet",
      cloudstaff: "Cloudstaff",
      emapta: "Emapta",
      bossjob: "BossJob.ph",
      kalibrr: "Kalibrr",
      findtalent: "FindTalent.ph",
      hiretalent: "HireTalent.ph",
      ofmjobs: "OFM Jobs",
    };

    const getSourceLabel = (source) => SOURCE_LABELS[source] || source || "Unknown";

    const getSourceLogo = (source) => {
      const map = {
        olj:        '/images/job-source-logos/olj-icon-2.png',
        jobstreet:  '/images/job-source-logos/jobstreet-icon.png',
        cloudstaff: '/images/job-source-logos/cloudstaff-logo-emblem.svg',
        emapta:     '/images/job-source-logos/emapta-logo.svg',
        bossjob:    '/images/job-source-logos/bossjob-icon.png',
        kalibrr:    '/images/job-source-logos/kalibrr-logo.png',
        findtalent: '/images/job-source-logos/findtalent-icon.svg',
        hiretalent: '/images/job-source-logos/hiretalent-logo.jpg',
        ofmjobs:    '/images/job-source-logos/ofmjobs-icon.svg',
        upwork:     '/images/job-source-logos/upwork-icon.svg',
      };
      return map[source] ?? null;
    };

    const getStatusCount = (filter) => {
      if (filter === "all") {
        return applications.value.length;
      }
      return applications.value.filter((app) => app.status === filter).length;
    };

    const getJobDetailPath = (application) => {
      if (application?.job) return buildJobDetailsPath(application.job_title, application.job);
      return '/job-filters';
    };

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";

      const date = new Date(dateString);
      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    };

    // View OLJ thread messages
    const viewMessages = async (application) => {
      const hashId = application.source_thread_hash_id;
      if (!hashId) return;
      showMessagesPanel.value = true;
      selectedOljHashId.value = hashId;
      selectedOljPartner.value = application.employer_name
        ? { name: application.employer_name }
        : null;
      oljThreadLoading.value = true;
      oljThreadData.value = null;
      oljThreadError.value = null;
      try {
        const res = await apiClient.get(`/olj/messages/thread/${hashId}/`, {
          params: { page: 1, per_page: 20 },
        });
        oljThreadData.value = res.data;
      } catch (err) {
        const status = err.response?.status;
        oljThreadError.value = status === 404 ? "not_found" : "error";
      } finally {
        oljThreadLoading.value = false;
      }
    };

    // View cover letter in slide-in panel
    const viewCoverLetter = (application) => {
      if (!application.cover_letter_content) return;
      selectedCoverLetter.value = {
        jobTitle: application.job_title || "Application",
        subject: application.cover_letter_subject || null,
        content: application.cover_letter_content,
      };
      showCoverLetterPanel.value = true;
    };

    // Update status directly from dropdown
    const updateStatus = async (application) => {
      try {
        await apiClient.patch(
          `/job-applications/${application.id}/update_status/`,
          { status: application.status },
        );

        // Show success toast
        toastType.value = "success";
        toastTitle.value = "Status Updated";
        toastMessage.value = `Application status updated to ${getStatusLabel(
          application.status,
        )}.`;
        showToast.value = true;

        // Hide after a few seconds
        setTimeout(() => {
          showToast.value = false;
        }, 5000);
      } catch (err) {
        console.error("Error updating application status:", err);

        // Show error toast and revert status
        toastType.value = "error";
        toastTitle.value = "Update Failed";
        let errorMessage = "Failed to update application status.";
        if (err.response) {
          errorMessage += ` (Status: ${err.response.status})`;
          console.error("Error response:", err.response.data);
        }
        toastMessage.value = errorMessage;
        showToast.value = true;

        // Refresh to get the current status
        fetchApplications();
      }
    };

    // API calls
    const fetchApplications = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await apiClient.get(
          "/job-applications/list_applications/",
        );
        applications.value = response.data;
      } catch (err) {
        let errorMessage =
          "Failed to load your applications. Please try again.";
        if (err.response) {
          errorMessage += ` (Status: ${err.response.status})`;
        } else if (err.request) {
          errorMessage += " (No response received)";
        } else {
          errorMessage += ` (${err.message})`;
        }
        error.value = errorMessage;
      } finally {
        loading.value = false;
      }
    };

    const toggleSortDirection = () => {
      sortDir.value = sortDir.value === "desc" ? "asc" : "desc";
    };

    const resetFilters = () => {
      activeFilter.value = "all";
      searchQuery.value = "";
      sortKey.value = "applied_date";
      sortDir.value = "desc";
      currentPage.value = 1;
    };

    // Status update modal
    const showUpdateModal = ref(false);
    const selectedApplication = ref(null);
    const updatedStatus = ref("");
    const updatedNotes = ref("");
    const updatedInterviewDate = ref("");
    const updatedOfferDate = ref("");
    const updating = ref(false);

    const openUpdateModal = (application) => {
      selectedApplication.value = application;
      updatedStatus.value = application.status;
      updatedNotes.value = application.notes || "";

      // Format datetime-local inputs
      if (application.interview_date) {
        updatedInterviewDate.value = new Date(application.interview_date)
          .toISOString()
          .slice(0, 16);
      } else {
        updatedInterviewDate.value = "";
      }

      if (application.offer_date) {
        updatedOfferDate.value = new Date(application.offer_date)
          .toISOString()
          .slice(0, 16);
      } else {
        updatedOfferDate.value = "";
      }

      showUpdateModal.value = true;
    };

    const updateApplicationStatus = async () => {
      if (!selectedApplication.value) return;

      updating.value = true;

      try {
        const updateData = {
          status: updatedStatus.value,
          notes: updatedNotes.value,
        };

        // Add date fields based on status
        if (updatedStatus.value === "interview" && updatedInterviewDate.value) {
          updateData.interview_date = updatedInterviewDate.value;
        }

        if (updatedStatus.value === "offer" && updatedOfferDate.value) {
          updateData.offer_date = updatedOfferDate.value;
        }

        const response = await apiClient.patch(
          `/job-applications/${selectedApplication.value.id}/update_status/`,
          updateData,
        );

        // Update the application in the local array
        const index = applications.value.findIndex(
          (app) => app.id === selectedApplication.value.id,
        );
        if (index !== -1) {
          applications.value[index] = response.data;
        }

        // Show success toast
        toastType.value = "success";
        toastTitle.value = "Status Updated";
        toastMessage.value = `Application status updated to ${getStatusLabel(
          updatedStatus.value,
        )}.`;
        showToast.value = true;

        // Hide after a few seconds
        setTimeout(() => {
          showToast.value = false;
        }, 5000);

        // Close modal
        showUpdateModal.value = false;
      } catch (err) {
        console.error("Error updating application status:", err);

        // Show error toast with more details
        toastType.value = "error";
        toastTitle.value = "Update Failed";
        let errorMessage = "Failed to update application status.";
        if (err.response) {
          errorMessage += ` (Status: ${err.response.status})`;
          console.error("Error response:", err.response.data);
        }
        toastMessage.value = errorMessage;
        showToast.value = true;

        // Hide after a few seconds
        setTimeout(() => {
          showToast.value = false;
        }, 5000);
      } finally {
        updating.value = false;
      }
    };

    // Initialize
    onMounted(() => {
      fetchApplications();
    });

    return {
      // Data
      applications,
      loading,
      error,

      // Filters
      activeFilter,
      statusFilters,
      filteredApplications,
      displayedApplications,
      paginatedApplications,
      searchQuery,
      sortKey,
      sortDir,
      toggleSortDirection,
      resetFilters,
      currentPage,
      perPage,
      totalPages,
      visiblePages,

      // Status update
      showUpdateModal,
      selectedApplication,
      updatedStatus,
      updatedNotes,
      updatedInterviewDate,
      updatedOfferDate,
      statusChoices,
      updating,

      // Messages panel
      showMessagesPanel,
      selectedOljHashId,
      oljThreadData,
      oljThreadLoading,
      oljThreadError,
      selectedOljPartner,
      viewMessages,

      // Cover letter panel
      showCoverLetterPanel,
      selectedCoverLetter,
      viewCoverLetter,

      // Toast
      showToast,
      toastType,
      toastTitle,
      toastMessage,

      // Methods
      getStatusLabel,
      getStatusClass,
      getStatusPillClass,
      getStatusSelectClass,
      getStatusSelectStyle,
      getStatusCount,
      getSourceLabel,
      getSourceLogo,
      getJobDetailPath,
      formatDate,
      fetchApplications,
      openUpdateModal,
      updateApplicationStatus,
      updateStatus,
    };
  },
};
</script>

<style scoped>
/* Cover letter sheet styles */
.whitespace-pre-line {
  font-size: 14px;
}

/* Status dropdown styles */
.app-status-select {
  appearance: none;
  width: 100%;
  min-width: 0;
  border: 1px solid rgba(229, 231, 235, 1);
  border-radius: 12px;
  padding: 10px 36px 10px 12px;
  font-size: 13px;
  line-height: 1.1;
  color: rgba(17, 24, 39, 1);
  background: white;
}

.app-status-select:focus {
  outline: none;
  box-shadow: 0 0 0 4px rgba(66, 153, 225, 0.14);
}

@media (min-width: 1024px) {
  .app-status-select {
    width: auto;
    min-width: 140px;
  }
}

/* Fade transition for toast and backdrop */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide-in sheet transition */
.slide-sheet-enter-active,
.slide-sheet-leave-active {
  transition: transform 0.2s ease;
}
.slide-sheet-enter-from,
.slide-sheet-leave-to {
  transform: translateX(100%);
}
</style>
